import type { UnavailabilityMark } from "../lib/dayGrid"
import type { StaffColumn } from "../lib/staffColumns"
import type { CalendarDate, WeekDate } from "../lib/siteTime"
import type { WeekSchedule } from "../lib/weekGrid"
import { SLOT_TYPES, slotTypeOf } from "../lib/slotTypes"
import type { SlotType } from "../lib/slotTypes"
import { SlotCell } from "./SlotCell"

/**
 * One person's week: a column per weekday, a row per slot, in time order.
 *
 * The same grid as `DayView` with the axis swapped — there, columns are the people on a team; here
 * they are the days of one person's week. Every column has the same rows, so the time labels are
 * read off the first one.
 */
export function WeekView({ weekDates, schedule, dayUnavailability, today, onSlotClick, bookable = false, bookableSlotTypes = SLOT_TYPES, loading = false }: {
  weekDates: WeekDate[]
  schedule: WeekSchedule
  /** Days with time this person is not working, keyed by ISO date. Said in the header as well as on the rows. */
  dayUnavailability: Record<CalendarDate, UnavailabilityMark>
  /** Today in the site's zone, so it can be marked. Not necessarily in this week. */
  today: CalendarDate
  onSlotClick: (isoDate: CalendarDate, slotIdx: number) => void
  /** Whether free rows offer to be booked into — a booking permission, with one site in view. */
  bookable?: boolean
  /**
   * The kinds of free row this user may book into (AB#779) — `bookableSlotTypes`. A free row of any
   * other kind is drawn, labelled, and not offered. Defaults to every kind.
   */
  bookableSlotTypes?: readonly SlotType[]
  /**
   * The week's appointments are still on their way. The headers and the time axis are drawn as
   * usual — who and when are known — and every cell as a placeholder until there is something to put
   * in it.
   */
  loading?: boolean
}) {
  const rows = schedule[weekDates[0]?.isoDate ?? ""] ?? []

  if (rows.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center p-8 text-sm text-muted-foreground">
        Nothing scheduled this week.
      </div>
    )
  }

  const gridTemplateColumns = `72px repeat(${weekDates.length}, minmax(140px, 1fr))`

  return (
    <div className="day-view-wrap" aria-busy={loading}>
      <div style={{ overflowX: "auto" }}>
        <div style={{ display: "grid", gridTemplateColumns, gap: 6 }}>
          {/* Day headers */}
          <div />
          {weekDates.map(day => {
            const away = dayUnavailability[day.isoDate]
            const isToday = day.isoDate === today
            return (
              <div
                key={day.isoDate}
                className="staff-col-header"
                style={{ background: isToday ? TODAY.color : WEEKDAY.color }}
              >
                <div style={{ fontSize: "1rem" }}>{day.day.slice(0, 3)}</div>
                <div style={{ fontSize: "0.78rem", marginTop: 2 }}>{day.date}</div>
                {/* Said at the top as well as on every row, so somebody scanning the header sees
                    which days they are in without reading down each column. */}
                {away && (
                  <div style={{ fontSize: "0.62rem", marginTop: 2, opacity: 0.85 }}>{away.label}</div>
                )}
              </div>
            )
          })}

          {/* Time rows */}
          {rows.map((row, slotIdx) => (
            <div key={row.startMinutes} style={{ display: "contents" }}>
              <div className="time-label">{row.time}</div>
              {weekDates.map(day => {
                const cell = schedule[day.isoDate]?.[slotIdx]
                if (!cell) return <div key={`${day.isoDate}-${slotIdx}`} />
                return (
                  <SlotCell
                    key={`${day.isoDate}-${slotIdx}`}
                    cell={cell}
                    staff={day.isoDate === today ? TODAY : WEEKDAY}
                    bookable={bookable && bookableSlotTypes.includes(slotTypeOf(cell))}
                    loading={loading}
                    onClick={() => onSlotClick(day.isoDate, slotIdx)}
                  />
                )
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// Every appointment on this grid belongs to the same person, so colour carries no identity here the
// way it does on the team view — one colour for the week, and a second only to pick today out.
// Both are --staff-N pairs from bookingCalendar.css, so the week sits in the same palette.
const WEEKDAY: StaffColumn = {
  id: "week", calUserId: 0, name: "", initials: "", accepted: true,
  color: "#0369a1", light: "#e0f2fe",
}

const TODAY: StaffColumn = {
  ...WEEKDAY, id: "today", color: "#7c3aed", light: "#ede9fe",
}
